import React, { useEffect, useState } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Space, Tag,
  Typography, Alert, Popconfirm, message, Empty, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, WarningOutlined, DeleteOutlined, EditOutlined, KeyOutlined, DownloadOutlined } from '@ant-design/icons';
import { fiscalAdminService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Title } = Typography;
const { Text } = Typography;

/**
 * Alta y administración de perfiles fiscal-admin.
 *
 * La cartera se define por ASIGNACIÓN DIRECTA de usuarios: un usuario tiene un
 * solo fiscal-admin gestor. Otorgar, modificar o retirar un perfil de staff es
 * exclusivo del Admin.
 */
const FiscalAdmins = () => {
  const [perfiles, setPerfiles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [sinGestor, setSinGestor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [modalAlta, setModalAlta] = useState(false);
  const [carteraEnEdicion, setCarteraEnEdicion] = useState(null);
  const [passwordEnEdicion, setPasswordEnEdicion] = useState(null);
  // Fiscal-admin cuyo reporte se está pidiendo, y el periodo elegido.
  const [reporteDe, setReporteDe] = useState(null);
  const [periodoReporte, setPeriodoReporte] = useState(null);
  const [descargando, setDescargando] = useState(false);
  const [form] = Form.useForm();
  const [formCartera] = Form.useForm();
  const [formPassword] = Form.useForm();

  useEffect(() => {
    fetchTodo();
  }, []);

  const fetchTodo = async () => {
    try {
      const [perfilesData, usuariosData, sinGestorData] = await Promise.all([
        fiscalAdminService.listFiscalAdmins(),
        fiscalAdminService.getAssignableUsers(),
        fiscalAdminService.getUsuariosSinGestor(),
      ]);
      setPerfiles(perfilesData || []);
      // Solo usuarios no-staff son asignables como cartera.
      setUsuarios((usuariosData || []).filter((u) => !u.staff_role));
      setSinGestor(sinGestorData);
    } catch (err) {
      setError('Error al cargar los perfiles fiscal-admin');
      console.error('Error fetching fiscal-admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.createFiscalAdmin(values);
      message.success('Perfil fiscal-admin creado');
      setModalAlta(false);
      form.resetFields();
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo crear el perfil');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarCartera = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.updateCartera(carteraEnEdicion.id, values.client_ids || []);
      message.success('Cartera actualizada');
      setCarteraEnEdicion(null);
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar la cartera');
    } finally {
      setGuardando(false);
    }
  };

  const handleResetPassword = async (values) => {
    setGuardando(true);
    try {
      await fiscalAdminService.resetPassword(passwordEnEdicion.id, values.password);
      message.success('Contraseña actualizada');
      setPasswordEnEdicion(null);
      formPassword.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo actualizar la contraseña');
    } finally {
      setGuardando(false);
    }
  };

  const handleRetirar = async (id) => {
    try {
      await fiscalAdminService.removeFiscalAdmin(id);
      message.success('Perfil de staff retirado');
      await fetchTodo();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo retirar el perfil');
    }
  };

  const opcionesUsuarios = (usuarios || []).map((u) => ({
    label: `${u.name} (${u.email})`,
    value: u.id,
  }));

  /**
   * Descarga la relación del periodo para cobrarle a un gestor.
   *
   * El periodo se elige a mano y no se asume el mes corriente: se cobra lo ya
   * presentado, que casi nunca es el mes en curso.
   */
  const descargarReporte = async () => {
    if (!reporteDe || !periodoReporte) return;

    setDescargando(true);
    try {
      const periodoTexto = periodoReporte.format('YYYY-MM');
      const response = await fiscalAdminService.descargarReporteDeclaraciones(
        reporteDe.id,
        periodoTexto
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `declaraciones-${periodoTexto}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setReporteDe(null);
    } catch (err) {
      message.error('No se pudo descargar el reporte');
    } finally {
      setDescargando(false);
    }
  };

  const columns = [
    { title: 'Nombre', dataIndex: 'name', key: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: 'Correo', dataIndex: 'email', key: 'email' },
    {
      title: 'Cartera',
      dataIndex: 'cartera_clientes',
      key: 'cartera',
      render: (clientes) =>
        clientes?.length
          ? clientes.slice(0, 5).map((c) => <Tag color="cyan" key={c.id}>{c.name}</Tag>)
            .concat(clientes.length > 5 ? [<Tag key="mas">+{clientes.length - 5}</Tag>] : [])
          : <Tag>Sin usuarios</Tag>,
    },
    { title: 'Usuarios', dataIndex: 'clientes_en_cartera', key: 'clientes' },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, perfil) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setCarteraEnEdicion(perfil);
              formCartera.setFieldsValue({
                client_ids: (perfil.cartera_clientes || []).map((c) => c.id),
              });
            }}
          >
            Cartera
          </Button>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => {
              setPasswordEnEdicion(perfil);
              formPassword.resetFields();
            }}
          >
            Contraseña
          </Button>
          {/* La relación para cobrarle a ESTE gestor. Vive aquí y no en su
              agenda porque es la base de su factura: el cobrado no se genera su
              propia cuenta. El servidor lo impone con AdminMiddleware; esconder
              el botón nunca fue la barrera. */}
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => {
              setReporteDe(perfil);
              setPeriodoReporte(null);
            }}
          >
            Declaraciones presentadas
          </Button>
          <Popconfirm
            title="¿Retirar el perfil de staff?"
            description="La cuenta se conserva; sus usuarios quedan sin gestor."
            onConfirm={() => handleRetirar(perfil.id)}
            okText="Retirar"
            cancelText="Cancelar"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Retirar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Un usuario sin gestor queda sin gestión y sin error visible */}
      {sinGestor?.total > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Hay usuarios sin gestor asignado"
          description={
            <>
              <div style={{ marginBottom: 8 }}>
                Estos usuarios no tienen fiscal-admin asignado, así que no aparecen en ninguna cartera:
              </div>
              {sinGestor.usuarios.slice(0, 20).map((u) => (
                <Tag color="orange" key={u.id}>{u.name}</Tag>
              ))}
              {sinGestor.total > 20 && <Tag color="orange">+{sinGestor.total - 20}</Tag>}
            </>
          }
        />
      )}

      <Card
        title={<Title level={4} style={{ margin: 0 }}>Perfiles fiscal-admin</Title>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAlta(true)}>
            Nuevo fiscal-admin
          </Button>
        }
      >
        <ResponsiveTable
          rowKey="id"
          loading={loading}
          dataSource={perfiles}
          columns={columns}
          pagination={false}
          scroll={{ x: true }}
          locale={{ emptyText: <Empty description="Sin perfiles fiscal-admin" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        />
      </Card>

      <Modal
        title="Nuevo perfil fiscal-admin"
        open={modalAlta}
        onCancel={() => setModalAlta(false)}
        onOk={() => form.submit()}
        confirmLoading={guardando}
        okText="Crear"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={handleCrear}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Correo"
            rules={[{ required: true, message: 'Requerido' }, { type: 'email', message: 'Correo inválido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="password" label="Contraseña" rules={[{ required: true, message: 'Requerido' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="client_ids"
            label="Usuarios de la cartera"
            extra="Estos usuarios quedarán bajo su gestión. Se puede editar después."
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={opcionesUsuarios}
              placeholder="Selecciona usuarios"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Cartera de ${carteraEnEdicion?.name || ''}`}
        open={!!carteraEnEdicion}
        onCancel={() => setCarteraEnEdicion(null)}
        onOk={() => formCartera.submit()}
        confirmLoading={guardando}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={formCartera} layout="vertical" onFinish={handleGuardarCartera}>
          <Form.Item
            name="client_ids"
            label="Usuarios asignados"
            extra="Quitar un usuario lo deja sin gestor. Asignar uno que ya tenía otro gestor lo reasigna."
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={opcionesUsuarios}
              placeholder="Selecciona usuarios"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Contraseña de ${passwordEnEdicion?.name || ''}`}
        open={!!passwordEnEdicion}
        onCancel={() => setPasswordEnEdicion(null)}
        onOk={() => formPassword.submit()}
        confirmLoading={guardando}
        okText="Actualizar"
        cancelText="Cancelar"
      >
        <Form form={formPassword} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item
            name="password"
            label="Contraseña nueva"
            rules={[
              { required: true, message: 'Requerido' },
              { min: 6, message: 'Mínimo 6 caracteres' },
            ]}
            extra="La contraseña anterior deja de ser válida de inmediato."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Elegir el periodo antes de descargar. La tabla es una lista de
          personas, no de meses, así que el selector vive aquí. */}
      <Modal
        title={reporteDe ? `Declaraciones presentadas — ${reporteDe.name}` : 'Declaraciones presentadas'}
        open={Boolean(reporteDe)}
        onCancel={() => setReporteDe(null)}
        okText="Descargar CSV"
        cancelText="Cancelar"
        onOk={descargarReporte}
        okButtonProps={{ disabled: !periodoReporte, loading: descargando }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Esta es la relación con la que se le cobra"
            description="Incluye las declaraciones presentadas del periodo por la cartera de este gestor, y las que no se pudieron presentar por falta de e.firma. El fiscal-admin no puede descargarla."
          />

          <div>
            <Text>Periodo</Text>
            <DatePicker
              picker="month"
              style={{ width: '100%', marginTop: 4 }}
              value={periodoReporte}
              onChange={setPeriodoReporte}
              placeholder="Elige el mes que se va a cobrar"
              // Sin valor por defecto a propósito: se cobra lo ya presentado,
              // que casi nunca es el mes en curso. Traer el corriente invitaría
              // a descargar el mes equivocado sin mirar.
              disabledDate={(fecha) => fecha && fecha.isAfter(dayjs(), 'month')}
            />
          </div>
        </Space>
      </Modal>
    </Space>
  );
};

export default FiscalAdmins;
